import { useNavigate } from 'react-router-dom';
import { Check } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/molecules/tooltip';
import { cn } from '@/lib/utils';
import type { ProductGroupItem } from '@/lib/types';
import { useTranslation } from 'react-i18next';

interface ColorSwatchesProps {
  items: ProductGroupItem[];
  currentProductId: string;
  onProductClick?: (refId: string) => void;
}

export function ColorSwatches({ items, currentProductId, onProductClick }: ColorSwatchesProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleClick = (refId: string) => {
    if (refId !== currentProductId) {
      if (onProductClick) {
        onProductClick(refId);
      } else {
        navigate(`/products/${refId}`);
      }
    }
  };

  return (
    <TooltipProvider>
      <div className="flex gap-2 flex-wrap">
        {items.map((item) => {
          const isCurrent = item.product.refId === currentProductId;
          const isWhite = item.colorHex?.toLowerCase() === '#ffffff' || item.colorHex?.toLowerCase() === '#fff';
          const isYellow = item.colorHex?.toLowerCase() === '#ffff00';

          return (
            <Tooltip key={item.id}>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => handleClick(item.product.refId)}
                  className={cn(
                    'size-8 rounded-full flex items-center justify-center transition-all',
                    'border-2',
                    isCurrent
                      ? 'border-primary ring-2 ring-primary/20'
                      : isWhite
                      ? 'border-gray-300 hover:border-primary'
                      : 'border-transparent hover:border-primary',
                    !isCurrent && 'cursor-pointer hover:scale-110',
                    isCurrent && 'cursor-default'
                  )}
                  style={{
                    backgroundColor: item.colorHex || '#ccc',
                    boxShadow: isWhite ? 'inset 0 0 0 1px rgba(0,0,0,0.1)' : undefined,
                  }}
                >
                  {isCurrent && (
                    <Check
                      className={cn(
                        'size-4',
                        isWhite || isYellow ? 'text-gray-800' : 'text-white'
                      )}
                    />
                  )}
                </button>
              </TooltipTrigger>
              <TooltipContent>
                {isCurrent ? t('variations.currentProduct') : item.colorName || t('common.unknown')}
              </TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    </TooltipProvider>
  );
}
